import type { PROTOCOLS } from "./constants.js";

/**
 * Request message sent between client and server systems.
 *
 * @example
 * const req: ClientServerRequest = { type: 'PING', to: 'PingSystem', data: {} };
 */
export type ClientServerRequest<T = any> = {
    type: string | number,
    to: string | number,
    data: T,
}

/**
 * Serialized protocol message array (protocol enum + args).
 */
export type ProtocolArray = [PROTOCOLS, ...any[]];

/**
 * Constructor type helper.
 */
export type Constructor<T = {}> = new (...args: any[]) => T;

/**
 * Local message routed between systems in the same process.
 */
export type LocalSystemMessage = {
    type: string | number,
    to: string | number | string[] | number[] | (string | number)[],
    data: any,
}

/**
 * Local system message after routing.
 */
export type ReceivedLocalSystemMessage = {
    type: string | number,
    data: any,
}

/**
 * Local message broadcast to all systems.
 */
export type GlobalLocalSystemMessage = Omit<LocalSystemMessage, 'to'>;

/**
 * Area request payload (client/server area messaging).
 */
export type AreaSystemRequest<T = any> = {
    areaId?: string | number,
    type: string | number,
    to?: string | number,
    data: T,
}

/**
 * Mix-in behavior for merging class methods.
 */
export enum MixinMethodMode {
  EXTEND,
  OVERRIDE
}

/**
 * Area request data payload.
 */
export type AreaRequestData<T = any> = {
    type: string | number,
    to?: string | number,
    data: T,
}

/**
 * Message with optional target.
 */
export type OptionalSystemMessage<T = any> = {
    type: string | number,
    data: T,
    to?: string | number,
}

/**
 * System message with explicit target.
 */
export type SystemMessage<T = any> = {
    type: string | number,
    to: string | number,
    data: T,
}

/**
 * System message with optional target.
 */
export type MaybeSystemMessage<T = any> = {
    type: string | number,
    to?: string | number,
    data: T,
}
/**
 * System request payload.
 */
export type SystemRequest<T = any> = {
    type: string | number,
    to: string | number,
    data: T,
}

/**
 * Represents a message received from the network.
 * The `data` field is typed as `unknown` by default — use type assertions or
 * supply a generic to narrow:
 * @example
 * onServerMessage(message: ReceivedMessage): void {
 *   const data = message.data as PlayerListData;
 * }
 * // or with generic:
 * onServerMessage(message: ReceivedMessage<PlayerListData>): void {
 *   message.data.players; // typed!
 * }
 */
export type ReceivedMessage<T = unknown> = {
  type: string | number,
  data: T,
}

/**
 * Data a server system receives when a client joins its area.
 *
 * id - public player id used for communicating [area] <-> [connector] <-> [clients]
 * auth - current auth object, originates from whatever the gate's onAuthenticate returned
 * options - options that were returned from the area file's onJoinRequest
 */
export type ClientJoinedData<Auth = any, Options = any> = {
  id: string | number,
  auth: Auth,
  options: Options
}

/**
 * Game listing data returned by Gate server.
 */
export type GameGateData = {
  id: GameId,
  type: GameType,
  clientData: any,
  meta?: any,
  /** Dot-path to the durable identity in this game's server-verified auth — the
   *  gate matches an automatic rejoin on it, so a returning player is recognised
   *  across the NEW `privateId` a page reload mints. Declared on the game file
   *  (`ServerGameFile.authIdentityPath`) and pushed by master with `ADD_GAME`. */
  authIdentityPath?: string,
}

/**
 * JSON-serializable payload type.
 * Represents any value that can be safely serialized to JSON.
 *
 * @deprecated Message types now use generics (`SystemMessage<T=any>`, `ReceivedMessage<T=unknown>`).
 * This type is kept for backward compatibility but is no longer used by framework message types.
 */
export type JSONEncodable =
  | string
  | number
  | boolean
  | null
  | JSONEncodable[]
  | { [key: string]: JSONEncodable }
  | { toJSON(): JSONEncodable };

export enum PEER_CONNECTION_STATUS {
    NONE,
    WAITING_FOR_INITIAL_RESPONSE,
    WAITING_FOR_HANDSHAKE,
    CONNECTED,
    FAILED
}

/** Game instance identifier type. */
export type GameId = string | number
/** Area instance identifier type. */
export type AreaId = string | number
/** Game type identifier type. */
export type GameType = string | number
/** Area type identifier type. */
export type AreaType = string | number

/**
 * Branded phantom type used by `api<T>()` to carry API type information
 * without any runtime cost. Used with `ClientSystem` / `ServerSystem` overloads.
 */
declare const __API_BRAND__: unique symbol;
/** @internal Phantom wrapper — carries a type `T` at the type level only. */
export type ApiType<T> = { readonly [__API_BRAND__]?: T };

/**
 * Zero-runtime helper that carries an API type for use as a second argument
 * to `ClientSystem()` or `ServerSystem()` when extending a shared base class
 * and widening the API surface.
 *
 * @example
 * ```typescript
 * type FullApi = BaseApi & { onDraw: (cb: () => void) => void };
 * class MySystem extends ClientSystem(SharedBase, api<FullApi>()) {
 *   // this.$api has all SharedBase API keys + FullApi keys
 * }
 * ```
 */
export function api<T>(): ApiType<T> { return undefined as unknown as ApiType<T>; }